// Google Analytics utility functions

// Extend the Window interface to include gtag
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Check if gtag is available
 */
const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a page view
 * @param pagePath - The path of the page being viewed
 * @param pageTitle - Optional title of the page
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  if (!isGtagAvailable()) {
    console.log('[GA - Dev] Page View:', { pagePath, pageTitle });
    return;
  }

  window.gtag!('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

/**
 * Track a custom event
 * @param eventName - Name of the event
 * @param eventParams - Additional parameters for the event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
): void => {
  if (!isGtagAvailable()) {
    console.log('[GA - Dev] Event:', eventName, eventParams);
    return;
  }

  window.gtag!('event', eventName, eventParams);
};

/**
 * Track a button click
 * @param buttonName - Name/label of the button
 * @param buttonLocation - Where the button is located (e.g., 'hero', 'cta', 'footer')
 * @param additionalParams - Any additional tracking parameters
 */
export const trackButtonClick = (
  buttonName: string,
  buttonLocation: string,
  additionalParams?: Record<string, unknown>
): void => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation,
    ...additionalParams,
  });
};

/**
 * Track CTA (Call-to-Action) interactions
 * @param ctaName - Name of the CTA
 * @param ctaType - Type of CTA (e.g., 'notify', 'shop', 'explore')
 * @param productName - Optional product name associated with the CTA
 */
export const trackCTAClick = (
  ctaName: string,
  ctaType: string,
  productName?: string
): void => {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_type: ctaType,
    product_name: productName,
  });
};

/**
 * Track form submissions
 * @param formName - Name of the form
 * @param formStatus - Status of submission ('attempt', 'success', 'error')
 * @param errorMessage - Optional error message if status is 'error'
 */
export const trackFormSubmission = (
  formName: string,
  formStatus: 'attempt' | 'success' | 'error',
  errorMessage?: string
): void => {
  trackEvent('form_submission', {
    form_name: formName,
    form_status: formStatus,
    error_message: errorMessage,
  });
};

/**
 * Track modal interactions
 * @param modalName - Name of the modal
 * @param action - Action performed ('open', 'close', 'submit')
 * @param productName - Optional product name associated with the modal
 */
export const trackModalInteraction = (
  modalName: string,
  action: 'open' | 'close' | 'submit',
  productName?: string
): void => {
  trackEvent('modal_interaction', {
    modal_name: modalName,
    modal_action: action,
    product_name: productName,
  });
};

/**
 * Track navigation actions
 * @param navigationType - Type of navigation ('scroll', 'click', 'carousel')
 * @param destination - Destination or target of navigation
 */
export const trackNavigation = (
  navigationType: string,
  destination: string
): void => {
  trackEvent('navigation', {
    navigation_type: navigationType,
    destination,
  });
};

/**
 * Track carousel interactions
 * @param carouselName - Name/location of the carousel
 * @param action - Action performed ('next', 'previous', 'indicator')
 * @param currentSlide - Current slide index
 */
export const trackCarouselInteraction = (
  carouselName: string,
  action: 'next' | 'previous' | 'indicator',
  currentSlide: number
): void => {
  trackEvent('carousel_interaction', {
    carousel_name: carouselName,
    carousel_action: action,
    current_slide: currentSlide,
  });
};
